import type { FC } from "react";
import { useState, useCallback, useRef, useEffect } from "react";

import Sentry from "@similarweb/sentry-logger";
import type { Textfield } from "@similarweb/ui-components";
import { StructureModal } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
// eslint-disable-next-line max-len
import { KeywordGeneratorToolPageModal } from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/KeywordGeneratorToolPageModal";
import { proModalStyles } from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordConst";
import { TextfieldStyled } from "pro-features/components/TableSelection/src/TextfieldStyled";

interface IKeywordGroupCreationModalProps {
    isOpen: boolean;
    isLoading: boolean;
    errorMessage: string | null;
    keywordsCount: number;
    defaultGroupName: string;
    onClose: () => void;
    onCreateGroup: (name: string) => Promise<void>;
    onViewGroup: () => void;
    onStartOver: () => void;
}

export const KeywordGroupCreationModal: FC<IKeywordGroupCreationModalProps> = ({
    isOpen,
    isLoading,
    errorMessage,
    keywordsCount,
    defaultGroupName,
    onClose,
    onCreateGroup,
    onViewGroup,
    onStartOver,
}) => {
    const [groupName, setGroupName] = useState(defaultGroupName);
    const [isNavigateModalOpen, setIsNavigateModalOpen] = useState(false);
    const [newlyCreatedGroup, setNewlyCreatedGroup] = useState(false);
    const textFieldRef = useRef<Textfield>(null);
    const maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount() as number;
    const isRowLimitExceeded = keywordsCount > maxKeywordsInGroup;
    const handleClose = useCallback(() => {
        setGroupName(defaultGroupName);
        setIsNavigateModalOpen(false);
        setNewlyCreatedGroup(false);
        onClose();
    }, [defaultGroupName, onClose]);

    const handleCreateGroup = useCallback(
        async (name: string) => {
            try {
                await onCreateGroup(name);
                setNewlyCreatedGroup(true);
                setIsNavigateModalOpen(true);
            } catch (error) {
                Sentry.error("Error in handleCreateGroup:", error);
            }
        },
        [onCreateGroup],
    );

    const handleViewGroup = useCallback(() => {
        onViewGroup();
        setIsNavigateModalOpen(false);
    }, [onViewGroup]);

    const handleStartOver = useCallback(() => {
        onStartOver();
        setIsNavigateModalOpen(false);
        setNewlyCreatedGroup(false);
    }, [onStartOver]);

    useEffect(() => {
        if (defaultGroupName && textFieldRef.current) {
            textFieldRef.current.setValue(defaultGroupName);
            setGroupName(defaultGroupName);
        }
    }, [defaultGroupName]);

    return (
        <>
            <StructureModal
                isOpen={isOpen}
                title={translate("table.selection.newgroup.keywords.create")}
                customStyles={proModalStyles}
                showHeaderDivider={false}
                mainButton={{
                    label: translate("table.selection.newgroup.confirm"),
                    onClick: () => handleCreateGroup(groupName),
                    type: "primary",
                    tooltipEnabled: false,
                    isLoading,
                    isDisabled: !groupName.trim(),
                }}
                secondaryButton={{
                    label: translate("table.selection.newgroup.cancel"),
                    onClick: handleClose,
                    type: "flatDark",
                    tooltipEnabled: false,
                }}
            >
                <Box>
                    <Text
                        margin="0 0 16px"
                        fontVariant="body1"
                        as="div"
                    >
                        {translate("table.selection.newgroup.keywords.create.label", {
                            numOfKeywords: isRowLimitExceeded ? maxKeywordsInGroup : keywordsCount,
                            keywordListName: groupName,
                        })}
                    </Text>
                    <TextfieldStyled
                        ref={textFieldRef}
                        error={!!errorMessage}
                        errorMessage={errorMessage}
                        placeholder={defaultGroupName}
                        defaultValue={groupName}
                        onChange={setGroupName}
                        dataAutomation="group-name-text-field"
                    />
                </Box>
            </StructureModal>
            {!errorMessage ? (
                <KeywordGeneratorToolPageModal
                    isOpen={isNavigateModalOpen}
                    onCloseClick={handleStartOver}
                    onViewGroupClick={handleViewGroup}
                    onStartOverClick={handleStartOver}
                    newlyCreatedGroup={newlyCreatedGroup}
                />
            ) : null}
        </>
    );
};
